import React, { useState } from 'react';
import { PurchaseOrderItemRecord } from '@/types/domain/po-management';
import { formatCurrency, formatDate } from '@/lib/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface PoItemsTableProps {
  items: PurchaseOrderItemRecord[];
  currency: string;
}

export const PoItemsTable: React.FC<PoItemsTableProps> = ({ items, currency }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="w-full overflow-x-auto rounded-2xl border border-border bg-card shadow-2xs font-heading">
      <table className="w-full text-left text-xs border-collapse">
        <thead className="bg-muted/50 text-foreground-secondary uppercase text-[10px] font-extrabold border-b border-border">
          <tr>
            <th className="py-3.5 px-4 w-10"></th>
            <th className="py-3.5 px-4">Line #</th>
            <th className="py-3.5 px-4">Material / Description</th>
            <th className="py-3.5 px-4 text-center">Ordered</th>
            <th className="py-3.5 px-4 text-center">Delivered</th>
            <th className="py-3.5 px-4 text-center">Pending</th>
            <th className="py-3.5 px-4 text-right">Unit Price</th>
            <th className="py-3.5 px-4 text-right">Line Total</th>
            <th className="py-3.5 px-4">Delivery Date</th>
            <th className="py-3.5 px-4 w-32">Progress</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {items.map((item) => {
            const isExpanded = !!expandedRows[item.id];
            const deliveredPct = Math.round((item.quantityDelivered / item.quantityOrdered) * 100);

            return (
              <React.Fragment key={item.id}>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="py-3.5 px-4 text-center">
                    <button
                      onClick={() => toggleRow(item.id)}
                      className="p-1 rounded hover:bg-muted text-foreground-muted"
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="py-3.5 px-4 font-mono font-bold text-foreground">{item.lineNumber}</td>
                  <td className="py-3.5 px-4 space-y-0.5">
                    <span className="font-mono font-bold text-primary block">{item.materialCode}</span>
                    <span className="font-bold text-foreground block">{item.description}</span>
                  </td>
                  <td className="py-3.5 px-4 text-center font-mono font-extrabold text-foreground">
                    {item.quantityOrdered} {item.uom}
                  </td>
                  <td className="py-3.5 px-4 text-center font-mono font-bold text-emerald-600">
                    {item.quantityDelivered} {item.uom}
                  </td>
                  <td className="py-3.5 px-4 text-center font-mono font-bold text-amber-600">
                    {item.quantityPending} {item.uom}
                  </td>
                  <td className="py-3.5 px-4 text-right font-mono font-bold text-foreground">
                    {formatCurrency(item.unitPrice, currency)}
                  </td>
                  <td className="py-3.5 px-4 text-right font-mono font-extrabold text-foreground">
                    {formatCurrency(item.lineTotal, currency)}
                  </td>
                  <td className="py-3.5 px-4 font-mono text-foreground-secondary">
                    {formatDate(item.deliveryDate)}
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] font-mono font-bold">
                        <span>{deliveredPct}%</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                          style={{ width: `${deliveredPct}%` }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Expanded Accounting Details */}
                {isExpanded && (
                  <tr className="bg-primary/5">
                    <td colSpan={10} className="p-4 space-y-3 border-t border-b border-primary/10">
                      <span className="text-[10px] font-extrabold text-primary uppercase tracking-wider block">
                        Line Item Accounting & Logistics Allocations
                      </span>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                        <div className="space-y-0.5">
                          <span className="text-[10px] font-bold text-foreground-muted block">Plant</span>
                          <span className="font-semibold text-foreground">{item.plant}</span>
                        </div>
                        <div className="space-y-0.5">
                          <span className="text-[10px] font-bold text-foreground-muted block">Storage Location</span>
                          <span className="font-semibold text-foreground">{item.storageLocation}</span>
                        </div>
                        <div className="space-y-0.5">
                          <span className="text-[10px] font-bold text-foreground-muted block">Cost Center</span>
                          <span className="font-semibold text-foreground">{item.costCenter}</span>
                        </div>
                        <div className="space-y-0.5">
                          <span className="text-[10px] font-bold text-foreground-muted block">GL Account</span>
                          <span className="font-semibold text-foreground">{item.glAccount}</span>
                        </div>
                      </div>

                      {item.specification && (
                        <div className="space-y-0.5 pt-2 border-t border-primary/10">
                          <span className="text-[10px] font-bold text-foreground-muted block">Technical Specification</span>
                          <p className="text-foreground-secondary text-[11px] font-medium leading-relaxed">
                            {item.specification}
                          </p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
